/**
 * In-flow "expand sidebar" button for pages that manage their own
 * top-left chrome (back arrow, page title, toolbar) and therefore
 * opt out of AppShell's global floating peek-trigger — see
 * `routeRendersOwnSidebarToggle` in shell/AppShell.tsx and the
 * `data-route='self-toggle'` override in styles/app.css.
 *
 * Renders nothing while the sidebar is expanded, so it can sit
 * unconditionally as the first child of a page header. Extracted
 * from the agent chat header (app/agent/[id]/page.tsx), which is
 * the reference implementation for this pattern.
 */

import type { CSSProperties } from 'react';
import { PanelLeftOpen } from 'lucide-react';
import { useSidebarCollapsed } from '@/lib/sidebarState';

export function SidebarExpandButton({ style }: { style?: CSSProperties }) {
    const [sidebarCollapsed, toggleSidebar] = useSidebarCollapsed();

    if (!sidebarCollapsed) return null;

    return (
        <button
            type="button"
            onClick={toggleSidebar}
            aria-label="Show sidebar"
            title="Show sidebar"
            style={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 32,
                height: 32,
                borderRadius: 8,
                background: 'transparent',
                border: 'none',
                color: 'var(--text-secondary)',
                cursor: 'pointer',
                flexShrink: 0,
                padding: 0,
                ...style,
            }}
        >
            <PanelLeftOpen size={16} strokeWidth={1.75} />
        </button>
    );
}
